import { Dayjs } from 'dayjs';
import { MutatableLegRaw } from '@api/Trinium/Endpoints/Leg/types';
import { ManagePickupDeliveryFormik } from '@/ManagePickupDelivery/types';
import { apiIsoStringFormat, dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';

/**
 * Method used for mapping a Leg type to a DispatchLeg type for optimistic updates when mutating the cache.
 */
export function mngPuDlToMutableLeg(form: Partial<ManagePickupDeliveryFormik>, originalDriverId?: string) {
	const updates: MutatableLegRaw = {
		drvId: form.assignedDriver ? form.assignedDriver.driver?.id : form.assignedDriver,
		tractorId: form.assignedDriver?.tractorId,
		// Scheduled Pickup From
		schdPuFrom:
			form.pickupForm?.scheduledDate !== undefined || form.pickupForm?.scheduledFromTime !== undefined
				? concatDateTime(form.pickupForm.scheduledDate, form.pickupForm.scheduledFromTime)
				: undefined,
		// Scheduled Pickup To
		schdPuTo:
			form.pickupForm?.scheduledToTime !== undefined
				? concatDateTime(
						form.pickupForm.scheduledDate,
						form.pickupForm.scheduledToTime,
						form.pickupForm.scheduledFromTime?.diff(form.pickupForm.scheduledToTime) === 0
					)
				: undefined,
		// Actual Pickup
		puDate: form.pickupForm?.actualDateTime?.format(apiIsoStringFormat) ?? undefined,
		puId: form.pickupForm?.location?.id ?? (form.pickupForm?.location as null | undefined),
		// Scheduled Delivery From
		schdDelvFrom:
			form.deliveryForm?.scheduledDate !== undefined || form.deliveryForm?.scheduledFromTime !== undefined
				? concatDateTime(form.deliveryForm.scheduledDate, form.deliveryForm.scheduledFromTime)
				: undefined,
		// Scheduled Delivery To
		schdDelvTo:
			form.deliveryForm?.scheduledToTime !== undefined
				? concatDateTime(
						form.deliveryForm.scheduledDate,
						form.deliveryForm.scheduledToTime,
						form.deliveryForm.scheduledFromTime?.diff(form.deliveryForm.scheduledToTime) === 0
					)
				: undefined,
		// Actual Delivery
		delvDate: form.deliveryForm?.actualDateTime?.format(apiIsoStringFormat) ?? undefined,
		delvId: form.deliveryForm?.location?.id ?? (form.deliveryForm?.location as null | undefined)
	};

	/**
	 * If only the tractor changed, apply the original driver id to the response because they are used in unison
	 * for a PUT request and we cannot only send the tractor ID
	 */
	if (updates.tractorId && !updates.drvId) {
		updates.drvId = originalDriverId;
	}

	Object.keys(updates).forEach((key) => {
		const asKey = key as keyof typeof updates;
		// If the value is undefined than remove it from the request
		if (updates[asKey] === undefined) {
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete updates[asKey];
		}
	});

	return updates;
}

const defaultTime = '00:00:00';

/**
 *
 * @param date Form input for Date
 * @param time Form input for From or To Time
 * @param addDay Flag indicating To & From time are the same, so add a day
 * @returns
 */
function concatDateTime(date?: Dayjs | null, time?: Dayjs | null, addDay?: boolean) {
	if (!date && !time) {
		return null;
	}

	let calculatedDate: Dayjs = date ? date : dayjsNow();
	if (addDay) {
		calculatedDate = calculatedDate.add(1, 'd');
	}

	let concatValue: string = calculatedDate.format('YYYY-MM-DD');

	concatValue = `${concatValue}T${time?.format('HH:mm:ss') ?? defaultTime}.000+00:00`;

	return concatValue;
}
